import React, { useEffect, useMemo, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Package, Gift, Sparkles, ShieldCheck, Ticket, GraduationCap, Users, Building2, Pencil, Save, Trash2, Plus, Crown } from "lucide-react";
import { toast } from "sonner";

const AUDIENCES = [
  { key: "parent", label: "Veli", icon: Users },
  { key: "student", label: "Öğrenci", icon: GraduationCap },
  { key: "school", label: "Okul", icon: Building2 },
];

export default function AdminSubscriptions() {
  const [modules, setModules] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState("parent");
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantPlan, setGrantPlan] = useState("mid");
  const [grantDays, setGrantDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({ price_monthly: 0, price_yearly: 0, description: "", highlight: false });
  const [creditPackages, setCreditPackages] = useState([]);
  const [editingPkg, setEditingPkg] = useState(null);
  const [pkgForm, setPkgForm] = useState({ credits: 0, price: 0, label: "", highlight: false });
  const [newPkgOpen, setNewPkgOpen] = useState(false);
  const [newPkgForm, setNewPkgForm] = useState({ code: "", credits: 5, price: 199, label: "", highlight: false });

  const load = async () => {
    const [m, p, c] = await Promise.all([
      api.get(`${V1}/modules`),
      api.get(`${V1}/subscription-plans`),
      api.get(`${V1}/credit-packages`),
    ]);
    setModules(m.data || []);
    setPlans(p.data || []);
    setCreditPackages(c.data || []);
  };

  useEffect(() => { load(); }, []);

  const audiencePlans = useMemo(() => plans.filter((p) => (p.audience || "parent") === tab), [plans, tab]);
  const audienceModules = useMemo(() => {
    if (tab === "school") return modules.filter((m) => m.target_role === "school");
    return modules.filter((m) => (m.target_role || "parent") === "parent");
  }, [modules, tab]);

  const patchModule = async (key, field, val) => {
    try {
      await api.patch(`${V1}/admin/modules/${key}`, { [field]: val });
      toast.success("Güncellendi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Güncellenemedi"); }
  };

  const patchModulePricing = async (key, price_monthly, price_yearly) => {
    try {
      await api.patch(`${V1}/admin/modules/${key}/pricing`, {
        price_monthly: parseInt(price_monthly) || 0,
        price_yearly: parseInt(price_yearly) || 0,
      });
      toast.success("Fiyat güncellendi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const openPlanEdit = (p) => {
    setEditingPlan(p);
    setPlanForm({
      price_monthly: p.price_monthly || 0,
      price_yearly: p.price_yearly || 0,
      description: p.description || "",
      highlight: !!p.highlight,
    });
  };

  const savePlan = async () => {
    try {
      await api.patch(`${V1}/admin/subscription-plans/${editingPlan.code}`, {
        price_monthly: parseInt(planForm.price_monthly) || 0,
        price_yearly: parseInt(planForm.price_yearly) || 0,
        description: planForm.description,
        highlight: planForm.highlight,
      });
      toast.success("Plan güncellendi");
      setEditingPlan(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const openPkgEdit = (p) => {
    setEditingPkg(p);
    setPkgForm({ credits: p.credits, price: p.price, label: p.label, highlight: !!p.highlight });
  };

  const savePkg = async () => {
    try {
      await api.patch(`${V1}/admin/credit-packages/${editingPkg.code}`, {
        credits: parseInt(pkgForm.credits) || 0,
        price: parseFloat(pkgForm.price) || 0,
        label: pkgForm.label,
        highlight: pkgForm.highlight,
      });
      toast.success("Kredi paketi güncellendi");
      setEditingPkg(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const deletePkg = async (code) => {
    if (!window.confirm(`${code} paketi silinsin mi?`)) return;
    try {
      await api.delete(`${V1}/admin/credit-packages/${code}`);
      toast.success("Paket silindi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const createPkg = async () => {
    if (!newPkgForm.code.trim() || !newPkgForm.label.trim()) { toast.error("Kod ve etiket gerekli"); return; }
    try {
      await api.post(`${V1}/admin/credit-packages`, {
        code: newPkgForm.code.trim().toLowerCase().replace(/\s+/g, "_"),
        credits: parseInt(newPkgForm.credits) || 0,
        price: parseFloat(newPkgForm.price) || 0,
        label: newPkgForm.label.trim(),
        highlight: newPkgForm.highlight,
      });
      toast.success("Yeni paket oluşturuldu");
      setNewPkgOpen(false);
      setNewPkgForm({ code: "", credits: 5, price: 199, label: "", highlight: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const grant = async () => {
    setBusy(true);
    try {
      const { data: users } = await api.get(`${V1}/admin/users?q=${encodeURIComponent(grantEmail)}`).catch(() => ({ data: [] }));
      const target = (users || []).find((u) => u.email === grantEmail || u.phone === grantEmail);
      if (!target) throw new Error("Bu e-posta / telefon ile kullanıcı bulunamadı");
      const source = grantDays === 7 ? "gift" : "admin_grant";
      await api.post(`${V1}/admin/users/${target.id}/subscription`, {
        plan_code: grantPlan, days: parseInt(grantDays), source,
      });
      toast.success(`${target.name || target.email} kullanıcısına ${grantDays} gün ${grantPlan} aboneliği verildi ✓`);
      setGrantOpen(false); setGrantEmail("");
    } catch (e) { toast.error(e?.message || e?.response?.data?.detail || "Verilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Abonelik & Modüller</div>
          <h1 className="font-display text-3xl font-bold">Fiyatlandırma Yönetimi</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Öğrenci, veli ve okul için ayrı planlar/modüller ve fiyatlar.
          </p>
        </div>
        <Button onClick={() => setGrantOpen(true)} data-testid="admin-grant-sub-btn">
          <Gift className="h-4 w-4" /> Abonelik Ver
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          {AUDIENCES.map((a) => (
            <TabsTrigger key={a.key} value={a.key} data-testid={`tab-${a.key}`}>
              <a.icon className="h-4 w-4 mr-1.5" /> {a.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {AUDIENCES.map((a) => (
          <TabsContent key={a.key} value={a.key} className="mt-6 space-y-6">
            {/* PLANS (only for parent + student) */}
            {a.key !== "school" && (
              <div className="grid md:grid-cols-3 gap-4">
                {audiencePlans.length === 0 && (
                  <div className="col-span-3 p-8 text-center text-muted-foreground border border-dashed border-border rounded-xl">
                    Bu segment için henüz plan yok.
                  </div>
                )}
                {audiencePlans.map((p) => (
                  <div key={p.code} className={`p-6 rounded-xl border ${p.highlight ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`} data-testid={`plan-card-${p.code}`}>
                    <div className="flex items-start justify-between">
                      <div className="overline text-accent flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> {p.name}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => openPlanEdit(p)} data-testid={`edit-plan-${p.code}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="font-display text-3xl font-bold mt-2">
                      {p.price_monthly === 0 ? "Ücretsiz" : `₺${p.price_monthly}`}
                      {p.price_monthly > 0 && <span className="text-sm font-normal text-muted-foreground">/ay</span>}
                    </div>
                    {p.price_yearly > 0 && (
                      <div className="text-xs text-muted-foreground">Yıllık: ₺{p.price_yearly}</div>
                    )}
                    <p className="text-sm text-muted-foreground mt-2">{p.description}</p>
                    <div className="mt-4 space-y-1 text-xs">
                      {audienceModules.map((m) => {
                        const has = (p.features || {})[m.key];
                        return (
                          <div key={m.key} className="flex items-center justify-between">
                            <span>{m.label}</span>
                            <span className={has?.unlimited ? "text-success font-bold" : has?.monthly_quota ? "text-accent font-bold" : "text-muted-foreground"}>
                              {has?.unlimited ? "Sınırsız" : has?.monthly_quota ? `${has.monthly_quota}/ay` : "Kredi ile"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* MODULES */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="p-4 border-b border-border font-display font-bold flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" /> {a.label} Modülleri
              </div>
              <div className="divide-y divide-border">
                {audienceModules.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Modül yok.</div>}
                {audienceModules.map((m) => (
                  <ModuleRow key={m.key} m={m} isSchool={a.key === "school"}
                    onToggle={(f, v) => patchModule(m.key, f, v)}
                    onEditCreditCost={(v) => patchModule(m.key, "credit_cost", v)}
                    onEditPricing={(pm, py) => patchModulePricing(m.key, pm, py)} />
                ))}
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* CREDIT PACKAGES — abonelikten bağımsız, veli & öğrenci hesapları içindir */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="admin-credit-packages-section">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div className="font-display font-bold flex items-center gap-2">
            <Ticket className="h-5 w-5 text-accent" /> Kredi Paketleri (Veli & Öğrenci)
          </div>
          <Button size="sm" onClick={() => setNewPkgOpen(true)} data-testid="new-credit-package-btn">
            <Plus className="h-4 w-4" /> Yeni Paket
          </Button>
        </div>
        <div className="divide-y divide-border">
          {creditPackages.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">Henüz kredi paketi yok.</div>
          )}
          {creditPackages.map((pkg) => (
            <div key={pkg.code} className="p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-credit-package-${pkg.code}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-medium flex items-center gap-1.5">
                  {pkg.label} {pkg.highlight && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                </div>
                <div className="text-xs text-muted-foreground font-mono">{pkg.code}</div>
              </div>
              <div className="text-sm"><b>{pkg.credits}</b> kredi</div>
              <div className="text-sm font-display font-bold">₺{pkg.price}</div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => openPkgEdit(pkg)} data-testid={`edit-credit-package-${pkg.code}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deletePkg(pkg.code)} data-testid={`delete-credit-package-${pkg.code}`}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Grant dialog */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent className="max-w-md" data-testid="grant-sub-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-accent" /> Abonelik Ver</DialogTitle>
            <DialogDescription>Kullanıcıya manuel abonelik atayın. 7 gün = hediye.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Kullanıcı E-posta veya Telefon</Label>
              <Input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="ornek@ornek.com veya 5XX..." data-testid="grant-email" />
            </div>
            <div>
              <Label>Plan</Label>
              <Select value={grantPlan} onValueChange={setGrantPlan}>
                <SelectTrigger data-testid="grant-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.code} value={p.code}>{p.name} ({p.audience || "parent"})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Süre (gün)</Label>
              <div className="grid grid-cols-4 gap-2">
                {[7, 15, 30, 90].map((d) => (
                  <Button key={d} type="button" variant={grantDays === d ? "default" : "outline"} size="sm"
                    onClick={() => setGrantDays(d)} data-testid={`grant-days-${d}`}>
                    {d === 7 ? "🎁 7" : d}
                  </Button>
                ))}
              </div>
              <Input type="number" className="mt-2" value={grantDays} onChange={(e) => setGrantDays(parseInt(e.target.value) || 0)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>Vazgeç</Button>
            <Button onClick={grant} disabled={busy || !grantEmail} data-testid="grant-confirm-btn">
              <ShieldCheck className="h-4 w-4" /> {busy ? "Veriliyor…" : "Aboneliği Ver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan edit dialog */}
      <Dialog open={!!editingPlan} onOpenChange={(o) => !o && setEditingPlan(null)}>
        <DialogContent className="max-w-md" data-testid="plan-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editingPlan?.name} — Fiyatlandırma</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Aylık Fiyat (₺)</Label>
                <Input type="number" value={planForm.price_monthly} onChange={(e) => setPlanForm({ ...planForm, price_monthly: e.target.value })} data-testid="plan-price-monthly" />
              </div>
              <div>
                <Label>Yıllık Fiyat (₺)</Label>
                <Input type="number" value={planForm.price_yearly} onChange={(e) => setPlanForm({ ...planForm, price_yearly: e.target.value })} data-testid="plan-price-yearly" />
              </div>
            </div>
            <div>
              <Label>Açıklama</Label>
              <Input value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={planForm.highlight} onCheckedChange={(v) => setPlanForm({ ...planForm, highlight: v })} />
              <span>Öne çıkar</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPlan(null)}>Vazgeç</Button>
            <Button onClick={savePlan} data-testid="save-plan-btn"><Save className="h-4 w-4" /> Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit package edit dialog */}
      <Dialog open={!!editingPkg} onOpenChange={(o) => !o && setEditingPkg(null)}>
        <DialogContent className="max-w-md" data-testid="credit-package-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editingPkg?.label} — Fiyatlandırma</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kredi Miktarı</Label>
                <Input type="number" value={pkgForm.credits} onChange={(e) => setPkgForm({ ...pkgForm, credits: e.target.value })} data-testid="pkg-credits" />
              </div>
              <div>
                <Label>Fiyat (₺)</Label>
                <Input type="number" value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} data-testid="pkg-price" />
              </div>
            </div>
            <div>
              <Label>Etiket</Label>
              <Input value={pkgForm.label} onChange={(e) => setPkgForm({ ...pkgForm, label: e.target.value })} data-testid="pkg-label" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={pkgForm.highlight} onCheckedChange={(v) => setPkgForm({ ...pkgForm, highlight: v })} />
              <span>Öne çıkar</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPkg(null)}>Vazgeç</Button>
            <Button onClick={savePkg} data-testid="save-credit-package-btn"><Save className="h-4 w-4" /> Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit package create dialog */}
      <Dialog open={newPkgOpen} onOpenChange={setNewPkgOpen}>
        <DialogContent className="max-w-md" data-testid="new-credit-package-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Kredi Paketi</DialogTitle>
            <DialogDescription>Abonelikten bağımsız, tek seferlik satın alınabilen kredi paketi.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Kod (benzersiz)</Label>
              <Input value={newPkgForm.code} onChange={(e) => setNewPkgForm({ ...newPkgForm, code: e.target.value })} placeholder="credit_20" data-testid="new-pkg-code" />
            </div>
            <div>
              <Label>Etiket</Label>
              <Input value={newPkgForm.label} onChange={(e) => setNewPkgForm({ ...newPkgForm, label: e.target.value })} placeholder="20 Kredi" data-testid="new-pkg-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kredi Miktarı</Label>
                <Input type="number" value={newPkgForm.credits} onChange={(e) => setNewPkgForm({ ...newPkgForm, credits: e.target.value })} data-testid="new-pkg-credits" />
              </div>
              <div>
                <Label>Fiyat (₺)</Label>
                <Input type="number" value={newPkgForm.price} onChange={(e) => setNewPkgForm({ ...newPkgForm, price: e.target.value })} data-testid="new-pkg-price" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={newPkgForm.highlight} onCheckedChange={(v) => setNewPkgForm({ ...newPkgForm, highlight: v })} />
              <span>Öne çıkar</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewPkgOpen(false)}>Vazgeç</Button>
            <Button onClick={createPkg} data-testid="create-credit-package-btn"><Plus className="h-4 w-4" /> Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ModuleRow = ({ m, isSchool, onToggle, onEditCreditCost, onEditPricing }) => {
  const [pm, setPm] = useState(m.price_monthly ?? 0);
  const [py, setPy] = useState(m.price_yearly ?? 0);
  const [cc, setCc] = useState(m.credit_cost ?? 0);
  useEffect(() => { setPm(m.price_monthly ?? 0); setPy(m.price_yearly ?? 0); setCc(m.credit_cost ?? 0); }, [m.key, m.price_monthly, m.price_yearly, m.credit_cost]);
  return (
    <div className="p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-module-${m.key}`}>
      <div className="flex-1 min-w-[220px]">
        <div className="font-medium">{m.label}</div>
        <div className="text-xs text-muted-foreground">{m.description}</div>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Aktif</span>
          <Switch checked={m.enabled} onCheckedChange={(v) => onToggle("enabled", v)} data-testid={`toggle-enabled-${m.key}`} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Ücretli</span>
          <Switch checked={m.paid} onCheckedChange={(v) => onToggle("paid", v)} data-testid={`toggle-paid-${m.key}`} />
        </label>
        {isSchool ? (
          <div className="flex items-center gap-2 text-sm">
            <Input type="number" min="0" className="w-20 h-8" value={pm} onChange={(e) => setPm(e.target.value)}
              onBlur={() => onEditPricing(pm, py)} data-testid={`price-monthly-${m.key}`} placeholder="Aylık" />
            <span className="text-[10px] text-muted-foreground">/ay</span>
            <Input type="number" min="0" className="w-20 h-8" value={py} onChange={(e) => setPy(e.target.value)}
              onBlur={() => onEditPricing(pm, py)} data-testid={`price-yearly-${m.key}`} placeholder="Yıllık" />
            <span className="text-[10px] text-muted-foreground">/yıl</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <Ticket className="h-3.5 w-3.5 text-accent" />
            <Input type="number" min="0" className="w-16 h-8" value={cc}
              onChange={(e) => setCc(e.target.value)}
              onBlur={() => onEditCreditCost(parseInt(cc) || 0)} data-testid={`credit-cost-${m.key}`} />
            <span className="text-[10px] text-muted-foreground">kredi</span>
          </div>
        )}
      </div>
    </div>
  );
};
